import { Icon } from '@iconify/react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import type { AdminMember } from '../../../types/ApiTypes'
import AddMemberModal from '../../components/AddMemberModal'
import SkeletonTable from '../../components/SkeletonTable'
import Dashboard from '../../layout/Dashboard'
import { userService } from '../../services/UserService'
import { ListHead, ListRow, NavContainer, NavWrapper } from '../../styles/CrypoAssetStyle'
import { Content, Heading } from '../../styles/OverviewStyle'
import { useAuth } from '../../hooks/useAuth'

export default function SettingsAdmin(): React.JSX.Element {
   const [addOpen, setAddOpen] = useState(false)
   const [admins, setAdmins] = useState<AdminMember[]>([])
   const [loading, setLoading] = useState(true)
   const [openMenuId, setOpenMenuId] = useState<string | null>(null)
   const menuRef = useRef<HTMLDivElement>(null)
   const navigate = useNavigate()
   const { isSuperAdmin } = useAuth()

   useEffect(() => {
      if (!isSuperAdmin) {
         navigate('/settings', { replace: true })
         return
      }
      loadAdmins()
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
   }, [isSuperAdmin, navigate])

   const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
         setOpenMenuId(null)
      }
   }

   const loadAdmins = async () => {
      try {
         const response = await userService.getAdminMembers()
         if (response.status === 'success' && response.data) {
            setAdmins(response.data)
         }
      } catch (error) {
         toast.error(error instanceof Error ? error.message : 'Failed to load admin members')
      } finally {
         setLoading(false)
      }
   }

   const handleResetPassword = async (id: string) => {
      try {
         const response = await userService.resetAdminPassword(id)
         if (response.status === 'success') {
            toast.success(response.message || 'Password reset successfully')
         } else {
            toast.error(response.message || 'Failed to reset password')
         }
      } catch (error) {
         toast.error(error instanceof Error ? error.message : 'Failed to reset password')
      }
      setOpenMenuId(null)
   }

   const handleResetPin = async (id: string) => {
      try {
         const response = await userService.resetAdminPin(id)
         if (response.status === 'success') {
            toast.success(response.message || 'PIN reset successfully')
         } else {
            toast.error(response.message || 'Failed to reset PIN')
         }
      } catch (error) {
         toast.error(error instanceof Error ? error.message : 'Failed to reset PIN')
      }
      setOpenMenuId(null)
   }

   const handleRemoveAdmin = async (id: string) => {
      try {
         const response = await userService.removeAdminMember(id)
         if (response.status === 'success') {
            toast.success(response.message || 'Admin removed successfully')
            setAdmins(prev => prev.filter(admin => admin.id !== id))
         } else {
            toast.error(response.message || 'Failed to remove admin')
         }
      } catch (error) {
         toast.error(error instanceof Error ? error.message : 'Failed to remove admin')
      }
      setOpenMenuId(null)
   }

   const formatLastLogin = (dateString: string) => {
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
         month: 'short',
         day: 'numeric',
         year: 'numeric',
         hour: 'numeric',
         minute: '2-digit',
         second: '2-digit',
         hour12: true
      })
   }

   const getRoleBadge = (role: string) => {
      const roleMap: Record<string, { label: string; color: string; bg: string }> = {
         superadmin: { label: 'Super-Admin', color: '#761BB8', bg: 'rgba(118,27,184,0.08)' },
         admin: { label: 'Admin', color: '#AF7AD5', bg: 'rgba(175,122,213,0.08)' }
      }
      const roleStyle = roleMap[role.toLowerCase()] || roleMap.admin!
      return (
         <span className="px-3 py-1 rounded-full text-xs font-geist-mono" style={{ backgroundColor: roleStyle.bg, color: roleStyle.color }}>
            {roleStyle.label}
         </span>
      )
   }

   const handleAddMember = () => {
      loadAdmins()
   }

   return (
      <>
         <Dashboard title="Settings / Admin" icon="solar:settings-linear" active="settings">
            <Content>
               <div className="flex items-center justify-between mb-2">
                  <Heading>Settings</Heading>
                  <button onClick={() => setAddOpen(true)} className="px-4 pl-3 py-2 rounded-full bg-[#AF7AD5] text-white text-sm font-semibold tracking-tight hover:bg-[#9b63c9] cursor-pointer inline-flex items-center gap-1">
                     <Icon icon="ic:outline-plus" width={19} />
                     Add Members
                  </button>
               </div>

               <NavContainer>
                  <NavWrapper children="General" onClick={() => navigate('/settings')} />
                  <NavWrapper children="Admin" $active />
               </NavContainer>

               <div className="mt-6">
                  <div className="text-base font-semibold text-[#101018] mb-3">Admin members</div>

                  <ListHead style={{ gridTemplateColumns: '1fr 1fr 140px 240px 40px' }}>
                     <div className="text-[12px] tracking-tight text-[#9C9C9F]">Full name</div>
                     <div className="text-[12px] tracking-tight text-[#9C9C9F]">Email</div>
                     <div className="text-[12px] tracking-tight text-[#9C9C9F]">Role</div>
                     <div className="text-[12px] tracking-tight text-[#9C9C9F]">Last login</div>
                     <div></div>
                  </ListHead>

                  {loading ? (
                     <SkeletonTable />
                  ) : admins.length === 0 ? (
                     <div className="text-center py-8 text-[#9C9C9F]">No admin members found</div>
                  ) : (
                     admins.map((admin) => (
                        <ListRow key={admin.id} style={{ gridTemplateColumns: '1fr 1fr 140px 240px 40px' }}>
                           <div className="text-sm text-[#6D6D72]">{admin.fullName}</div>
                           <div className="text-sm text-[#6D6D72]">{admin.emailAddress}</div>
                           <div className="text-sm">{getRoleBadge(admin.role)}</div>
                           <div className="text-xs text-[#9C9C9F]">{formatLastLogin(admin.lastLoginTime)}</div>
                           <div className="relative" ref={openMenuId === admin.id ? menuRef : null}>
                              <button
                                 onClick={() => setOpenMenuId(openMenuId === admin.id ? null : admin.id)}
                                 className="p-1 hover:bg-gray-100 rounded"
                              >
                                 <Icon icon="mdi:dots-vertical" width={20} className="text-[#6D6D72]" />
                              </button>
                              {openMenuId === admin.id && (
                                 <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                    <button
                                       onClick={() => handleResetPassword(admin.id)}
                                       className="w-full text-left px-4 py-2 text-sm text-[#6D6D72] hover:bg-gray-50 flex items-center gap-2"
                                    >
                                       <Icon icon="mdi:lock-reset" width={16} />
                                       Reset Password
                                    </button>
                                    <button
                                       onClick={() => handleResetPin(admin.id)}
                                       className="w-full text-left px-4 py-2 text-sm text-[#6D6D72] hover:bg-gray-50 flex items-center gap-2"
                                    >
                                       <Icon icon="mdi:pin" width={16} />
                                       Reset PIN
                                    </button>
                                    <button
                                       onClick={() => handleRemoveAdmin(admin.id)}
                                       className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                       <Icon icon="mdi:delete" width={16} />
                                       Remove Admin
                                    </button>
                                 </div>
                              )}
                           </div>
                        </ListRow>
                     ))
                  )}
               </div>
            </Content>
         </Dashboard>
         <AddMemberModal open={addOpen} onClose={() => setAddOpen(false)} onAdd={handleAddMember} />
      </>
   )
}
